import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

import { AddTierRateRequest, TierRateRow } from '../../finance.service';

export interface TierRateModalData {
  /** undefined → Add mode; populated → Edit mode (preloads the form). */
  row?: TierRateRow;
  /** Year preselected from the parent tab — applied to new rows so the
   *  master populates the year the operator was just looking at. */
  year: number;
}

export interface TierRateModalResult {
  payload: AddTierRateRequest;
  /** Echoed back so the parent calls update vs create without re-checking data. */
  id?: number;
}

/** Canonical tier list from Product. Free-text in the BE schema, so the
 *  select also offers a "Custom…" mode for new/legacy values. */
const CANONICAL_TIERS = ['Tier 1', 'Tier 2', 'Tier 3', 'Tier 4', 'Actuals', 'Vendor Billing', 'Non-Billable'];

@Component({
  selector: 'app-tier-rate-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
  ],
  templateUrl: './tier-rate-modal.component.html',
  styleUrl: './tier-rate-modal.component.scss',
})
export class TierRateModalComponent {
  readonly canonicalTiers = CANONICAL_TIERS;
  readonly yearOptions: number[];
  readonly isEdit: boolean;

  /** Toggle that swaps the Tier Model select for a free-text input when
   *  the operator wants a non-canonical name. */
  customTier = false;

  readonly form;

  constructor(
    private readonly fb: FormBuilder,
    private readonly dialogRef: MatDialogRef<TierRateModalComponent, TierRateModalResult | undefined>,
    @Inject(MAT_DIALOG_DATA) private readonly data: TierRateModalData,
  ) {
    this.form = this.fb.group({
      tierModel: this.fb.nonNullable.control('', [Validators.required, Validators.maxLength(64)]),
      targetYear: this.fb.nonNullable.control(
        new Date().getFullYear(),
        [Validators.required, Validators.min(2000), Validators.max(2100)],
      ),
      baseRate: new FormControl<number | null>(null),
    });
    this.isEdit = !!data.row;
    const now = new Date().getFullYear();
    const years: number[] = [];
    for (let y = now + 1; y >= now - 5; y--) years.push(y);
    this.yearOptions = years;

    if (data.row) {
      this.customTier = !CANONICAL_TIERS.includes(data.row.tierModel);
      this.form.patchValue({
        tierModel: data.row.tierModel,
        targetYear: data.row.targetYear,
        baseRate: data.row.baseRate ?? null,
      });
    } else {
      this.form.patchValue({ targetYear: data.year });
    }
  }

  toggleCustomTier(): void {
    this.customTier = !this.customTier;
    if (!this.customTier && !CANONICAL_TIERS.includes(this.form.controls.tierModel.value)) {
      this.form.controls.tierModel.setValue('');
    }
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const v = this.form.getRawValue();
    const payload: AddTierRateRequest = {
      tierModel: v.tierModel.trim(),
      targetYear: v.targetYear,
      baseRate: v.baseRate ?? undefined,
    };
    this.dialogRef.close({ payload, id: this.data.row?.id });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
